// The review panel's Finish tab (FR-14): where the case stands and what to do
// next. Pure, so the counts and suggestions are unit-tested.
import type { CaseDocument, CorrelationRisk, ReviewStatus, ReviewStatusDocument } from '../../types';
import { suggestedReviewDecision } from '../../types';
import { relevanceOf } from '../editor/relevance';

export interface FinishSummary {
  total: number;
  decided: number;
  undecided: number;
  responsiveFinalized: number;
  responsiveNotFinalized: number;
  /** Pending suggestions on responsive documents that aren't finalized yet. */
  pendingOnResponsive: number;
  openRisks: number;
  totalRisks: number;
  /** Responsive, not finalized, nothing stops finalizing it without a reviewer,
   * and it has approved redactions to apply. */
  finalizeReady: ReviewStatusDocument[];
  /** Like finalizeReady, but nothing is approved (and nothing pending): it
   * finalizes with no redactions (FR-02). */
  finalizeNoRedactions: ReviewStatusDocument[];
  /** Responsive, not finalized, but a reviewer must open it first (with why). */
  finalizeNeedsReview: ReviewStatusDocument[];
  /** Finalized, but a rule that matches its text changed since. */
  outOfDate: ReviewStatusDocument[];
  /** A Finalize / Re-finalize run is queued or running. */
  running: ReviewStatusDocument[];
  /** The last Finalize / Re-finalize run failed (with the error). */
  failed: ReviewStatusDocument[];
}

const isRunning = (d: ReviewStatusDocument) => d.batch?.status === 'queued' || d.batch?.status === 'running';

export function riskIsOpen(risk: CorrelationRisk, applied: boolean): boolean {
  return !risk.dismissed && !applied;
}

export function finishSummary(
  status: ReviewStatus | null,
  risks: { risk: CorrelationRisk; applied: boolean }[]
): FinishSummary {
  const docs = status?.documents ?? [];
  const responsive = docs.filter((d) => d.review_decision === 'responsive');
  const notFinalized = responsive.filter((d) => !d.finalized);
  return {
    total: docs.length,
    decided: docs.filter((d) => d.review_decision).length,
    undecided: docs.filter((d) => !d.review_decision).length,
    responsiveFinalized: responsive.filter((d) => d.finalized).length,
    responsiveNotFinalized: notFinalized.length,
    pendingOnResponsive: notFinalized.reduce((n, d) => n + d.pending_suggestions, 0),
    openRisks: risks.filter((r) => riskIsOpen(r.risk, r.applied)).length,
    totalRisks: risks.length,
    finalizeReady: notFinalized.filter((d) => !d.blocker && !isRunning(d) && d.approved_suggestions > 0),
    finalizeNoRedactions: notFinalized.filter((d) => !d.blocker && !isRunning(d) && d.approved_suggestions === 0),
    finalizeNeedsReview: notFinalized.filter((d) => d.blocker && !isRunning(d)),
    outOfDate: docs.filter((d) => d.out_of_date && !isRunning(d)),
    running: docs.filter(isRunning),
    failed: docs.filter((d) => d.batch?.status === 'failed'),
  };
}

/** How many suggestion cards Finish shows: Finalize and/or Re-finalize (the tab badge). */
export function suggestionCount(s: FinishSummary): number {
  return (s.responsiveNotFinalized > 0 ? 1 : 0) + (s.outOfDate.length > 0 ? 1 : 0);
}

/**
 * "Accept suggested" for one relevance group: the decision each undecided
 * document's score suggests, grouped so each decision is one bulk call.
 */
export function suggestedDecisions(documents: CaseDocument[]): { responsive: string[]; non_responsive: string[] } {
  const out = { responsive: [] as string[], non_responsive: [] as string[] };
  for (const d of documents) {
    if (d.review_decision) continue;
    const s = suggestedReviewDecision(relevanceOf(d));
    if (s === 'responsive' || s === 'non_responsive') out[s].push(d.id);
  }
  return out;
}

export function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}
